import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  Flag,
  Loader2,
  Plus,
  RefreshCw,
  Database,
  Trash2,
  CheckCircle2,
  XCircle,
  Inbox,
  Eye,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { TableColumnHeader } from "../../components/ui/TableColumnHeader";
import { MobileFilterBar } from "../../components/ui/MobileFilterBar";
import { Badge } from "../../components/ui/Badge";
import { EntityLink } from "../../components/ui/EntityLink";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";
import Input from "../../components/ui/Input";
import { reportsApi } from "../../utils/api";
import { EventStatusBadge } from "../../components/ui/EventStatusBadge";
import { formatDateTime, getAdminEntityUrl, getEventWebUrl } from "../../utils/helpers";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import CreateReportModal from "./CreateReportModal";
import {
  ENTITY_TYPE_META,
  REASON_LABELS,
  entityTypeFilterOptions,
  reasonFilterOptions,
} from "./constants";

const PAGE_SIZE = 20;

const REPORT_STATUS_META = {
  open: {
    label: "Open",
    variant: "warning",
    icon: Inbox,
    iconClass: "text-yellow-600",
    tileClass: "bg-yellow-500/10",
  },
  resolved: {
    label: "Resolved",
    variant: "success",
    icon: CheckCircle2,
    iconClass: "text-green-600",
    tileClass: "bg-green-500/10",
  },
  dismissed: {
    label: "Dismissed",
    variant: "secondary",
    icon: XCircle,
    iconClass: "text-gray-500",
    tileClass: "bg-gray-500/10",
  },
};

const statusSelectOptions = Object.entries(REPORT_STATUS_META).map(
  ([value, meta]) => ({ value, label: meta.label })
);

const statusFilterOptions = [
  { value: "", label: "All statuses" },
  ...statusSelectOptions,
];

const SORT_ORDER_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

// System-generated reports (missing_ticketing / multiple_links_provider /
// main_population_mismatch) have no reporting user; the API groups them under this sentinel.
const SYSTEM_REPORTER_ID = "__system__";

const reporterFilterOptions = (reporters) => [
  { value: "", label: "All reporters" },
  ...reporters.map((r) => ({
    value: r.user_id,
    label:
      r.user_id === SYSTEM_REPORTER_ID ? "System" : r.email || r.user_id,
  })),
];

// Rendered inside the table body (not in place of the table) so the column
// filters that produced the empty result stay on screen and can be undone.
function NoReports() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
      <Database className="h-12 w-12 mb-4" />
      <p className="text-lg font-medium">No reports found</p>
    </div>
  );
}

function ReportStatusBadge({ status }) {
  const meta = REPORT_STATUS_META[status];
  if (!meta) return <Badge variant="outline">{status}</Badge>;
  return (
    <Badge variant={meta.variant}>
      {meta.label}
    </Badge>
  );
}

function EntityTypeBadge({ entityType }) {
  const meta = ENTITY_TYPE_META[entityType];
  const Icon = meta?.icon;
  return (
    <Badge variant="outline" className="gap-1">
      {Icon && <Icon className="h-3 w-3" />}
      {meta?.label || entityType}
    </Badge>
  );
}

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters + sort + pagination
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [reporterFilter, setReporterFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  // Reporter dropdown options — the API only aggregates them on page 1, so keep
  // the last non-empty list while paging.
  const [reporters, setReporters] = useState([]);
  // Search by entity id. `idSearchInput` is the raw field; the debounced value is
  // what actually gets sent to the API (avoids a request per keystroke).
  const [idSearchInput, setIdSearchInput] = useState("");
  const idSearchFilter = useDebouncedValue(idSearchInput.trim(), 350);

  // Monotonic ids to discard out-of-order responses
  const reportsRequestRef = useRef(0);
  const entityRequestRef = useRef(0);

  // Detail modal
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [entityName, setEntityName] = useState(null);
  const [entityError, setEntityError] = useState(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const [newStatus, setNewStatus] = useState("open");
  const [adminNotes, setAdminNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [acceptingUrl, setAcceptingUrl] = useState(null);
  // Added this session — the report stays open, so the proposal itself records nothing to
  // distinguish a URL already on the event from one still to add.
  // Per-artist links the admin pruned before accepting, keyed by artist name:
  // { [name]: { urls: [removed url, ...], mb: bool } }. Per-accept only — reset
  // when the modal opens a report, never persisted.
  // Billing position the admin will stamp on each accepted artist, keyed by
  // artist name; "" = unknown/unranked. Prefilled from the agent's proposal on
  // modal open, always editable, per-accept only (never persisted).

  // Delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create modal
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await reportsApi.getStats());
    } catch (err) {
      console.error("Error fetching report stats:", err);
      setError(err.response?.data?.detail || "Error loading report stats");
    }
  }, []);

  const fetchReports = useCallback(async () => {
    const requestId = ++reportsRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE, sort_order: sortOrder };
      if (statusFilter) params.status = statusFilter;
      if (entityTypeFilter) params.entity_type = entityTypeFilter;
      if (reasonFilter) params.reason = reasonFilter;
      if (reporterFilter) params.reporter = reporterFilter;
      if (idSearchFilter) params.entity_id = idSearchFilter;
      const data = await reportsApi.getAll(params);
      if (requestId !== reportsRequestRef.current) return;
      const nextPagination = data.pagination || { total: 0, total_pages: 0 };
      setReports(data.reports || []);
      setPagination(nextPagination);
      if (data.reporters?.length) setReporters(data.reporters);
      // Clamp when the current page fell out of range (e.g. after deleting
      // the last report on the final page) — triggers a refetch via deps
      if (page > 1 && page > (nextPagination.total_pages || 0)) {
        setPage(Math.max(1, nextPagination.total_pages || 1));
      }
    } catch (err) {
      if (requestId !== reportsRequestRef.current) return;
      console.error("Error fetching reports:", err);
      setError(err.response?.data?.detail || "Error loading reports");
      setReports([]);
    } finally {
      if (requestId === reportsRequestRef.current) {
        setLoading(false);
      }
    }
  }, [
    page,
    statusFilter,
    entityTypeFilter,
    reasonFilter,
    reporterFilter,
    sortOrder,
    idSearchFilter,
  ]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const refresh = useCallback(
    () => Promise.all([fetchReports(), fetchStats()]),
    [fetchReports, fetchStats]
  );

  // Every column filter goes through here: set the value, back to page 1.
  const applyFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const setStatus = applyFilter(setStatusFilter);
  const setEntityType = applyFilter(setEntityTypeFilter);
  const setReason = applyFilter(setReasonFilter);
  const setReporter = applyFilter(setReporterFilter);

  // Stats card click toggles the status filter the header dropdown also drives
  const toggleStatusFilter = (status) =>
    setStatus(statusFilter === status ? "" : status);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setPage(1);
  };

  const handleIdSearchChange = (e) => {
    setIdSearchInput(e.target.value);
    setPage(1); // new search starts at page 1; the query itself is debounced
  };

  const clearFilters = () => {
    setStatusFilter("");
    setEntityTypeFilter("");
    setReasonFilter("");
    setReporterFilter("");
    setIdSearchInput("");
    setPage(1);
  };

  const hasActiveFilters = [
    statusFilter,
    entityTypeFilter,
    reasonFilter,
    reporterFilter,
    idSearchFilter,
  ].some(Boolean);

  // refresh() refetches with whatever filters are active, so a new (always `open`)
  // report can legitimately not appear — say so rather than showing a success
  // banner above a list that didn't change.
  const handleReportCreated = () => {
    setIsCreateOpen(false);
    setSuccess(
      hasActiveFilters
        ? "Report created — clear the filters to see it in the list"
        : "Report created"
    );
    refresh();
  };

  // One descriptor per column filter, rendered as a TableColumnHeader dropdown
  // on desktop and by MobileFilterBar below `lg`.
  const entityTypeColumnFilter = {
    value: entityTypeFilter,
    onChange: setEntityType,
    options: entityTypeFilterOptions,
  };

  const reasonColumnFilter = {
    value: reasonFilter,
    onChange: setReason,
    options: reasonFilterOptions,
  };

  const reporterColumnFilter = {
    value: reporterFilter,
    onChange: setReporter,
    options: reporterFilterOptions(reporters),
  };

  const statusColumnFilter = {
    value: statusFilter,
    onChange: setStatus,
    options: statusFilterOptions,
    align: "right",
  };

  // Mobile has no table header to hang the column filters off, so the same
  // descriptors render as chip rows below md — plus the date sort, which lives
  // on the Created column header on desktop.
  const mobileFilters = [
    { key: "entity_type", label: "Entity type", ...entityTypeColumnFilter },
    { key: "reason", label: "Reason", ...reasonColumnFilter },
    { key: "reporter", label: "Reporter", ...reporterColumnFilter },
    { key: "status", label: "Status", ...statusColumnFilter },
    { key: "sort_order", label: "Sort by date", value: sortOrder, onChange: applyFilter(setSortOrder), options: SORT_ORDER_OPTIONS },
  ];

  const fetchEntityName = async (report) => {
    const requestId = ++entityRequestRef.current;
    setEntityName(null);
    setEntityError(null);
    setEntityLoading(true);
    try {
      const name = await ENTITY_TYPE_META[report.entity_type]?.fetchName(
        report.entity_id
      );
      if (requestId !== entityRequestRef.current) return;
      if (name) {
        setEntityName(name);
      } else {
        setEntityError("Could not resolve the entity name");
      }
    } catch (err) {
      if (requestId !== entityRequestRef.current) return;
      setEntityError(
        err.response?.status === 404
          ? "This entity no longer exists"
          : "Failed to load the reported entity"
      );
    } finally {
      if (requestId === entityRequestRef.current) {
        setEntityLoading(false);
      }
    }
  };

  const handleOpenDetail = (report) => {
    setSelectedReport(report);
    setNewStatus(report.status);
    setAdminNotes(report.admin_notes || "");
    setIsDetailOpen(true);
    fetchEntityName(report);
  };

  const closeDetail = () => {
    setIsDetailOpen(false);
    setSelectedReport(null);
  };

  // Diff of the modal's editable fields vs. the selected report, recomputed
  // per render; also drives the Save button's disabled state via hasChanges.
  const pendingUpdates = {};
  if (selectedReport) {
    if (newStatus !== selectedReport.status) pendingUpdates.status = newStatus;
    if ((adminNotes || "") !== (selectedReport.admin_notes || "")) {
      pendingUpdates.admin_notes = adminNotes;
    }
  }
  const hasChanges = Object.keys(pendingUpdates).length > 0;

  // "View on Applauss" link for reported events; null when the web base URL
  // can't be resolved (the link is hidden rather than pointing at a bad host).
  const eventWebUrl =
    selectedReport?.entity_type === "event"
      ? getEventWebUrl(selectedReport.entity_id)
      : null;

  // Every URL the agent proposed (top candidate first, then alternates), deduped.
  const proposal = selectedReport?.agent_proposal;
  const proposedUrls = proposal
    ? [...new Set([proposal.candidate_url, ...(proposal.alternates || [])].filter(Boolean))]
    : [];

  const handleSave = async () => {
    if (!selectedReport || !hasChanges) return;
    setIsSaving(true);
    try {
      await reportsApi.update(selectedReport.id, pendingUpdates);
      setSuccess("Report updated successfully");
      closeDetail();
      refresh();
    } catch (err) {
      console.error("Error updating report:", err);
      setError(err.response?.data?.detail || "Error updating report");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptUrl = async (url) => {
    if (!selectedReport || acceptingUrl) return;
    setAcceptingUrl(url);
    try {
      await reportsApi.acceptProposal(selectedReport.id, url);
      setSuccess("URL added to the event and report resolved");
      closeDetail();
      refresh();
    } catch (err) {
      console.error("Error accepting proposal:", err);
      setError(err.response?.data?.detail || "Error accepting proposal");
    } finally {
      setAcceptingUrl(null);
    }
  };

  const handleOpenDeleteModal = (report) => {
    setSelectedReport(report);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedReport) return;
    setIsDeleting(true);
    try {
      await reportsApi.delete(selectedReport.id);
      setSuccess("Report deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedReport(null);
      refresh();
    } catch (err) {
      console.error("Error deleting report:", err);
      setError(err.response?.data?.detail || "Error deleting report");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Reports</h2>
          <p className="text-muted-foreground">
            Review and resolve reports, or file a new one
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Report
          </Button>
          <Button variant="outline" onClick={refresh} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {success && (
        <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
          {success}
          <button className="ml-2 underline" onClick={() => setSuccess(null)}>
            Dismiss
          </button>
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards — click a status card to filter the list */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Flag className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats ? stats.total : "-"}</p>
                <p className="text-sm text-muted-foreground">Total Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(REPORT_STATUS_META).map(([status, meta]) => {
          const active = statusFilter === status;
          return (
            <Card
              key={status}
              onClick={() => toggleStatusFilter(status)}
              className={`cursor-pointer transition-colors ${
                active ? "border-primary ring-1 ring-primary" : "hover:border-primary/50"
              }`}
            >
              <CardContent>
                <div className="flex items-center gap-4">
                  <div
                    className={`h-12 w-12 rounded-lg ${meta.tileClass} flex items-center justify-center`}
                  >
                    <meta.icon className={`h-6 w-6 ${meta.iconClass}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {stats ? stats[`${status}_count`] : "-"}
                    </p>
                    <p className="text-sm text-muted-foreground">{meta.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters — entity ID search is always available; the rest live in the
          table column headers on desktop and fall back to selects on mobile. */}
      <Card>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="w-full sm:w-64">
              <Input
                label="Entity ID"
                name="entity_id"
                value={idSearchInput}
                onChange={handleIdSearchChange}
                placeholder="Search by entity ID"
              />
            </div>
            <MobileFilterBar filters={mobileFilters} className="w-full lg:hidden" />
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            {pagination.total} {pagination.total === 1 ? "report" : "reports"}
            {hasActiveFilters ? " match the current filters" : " total"}
          </p>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">
                Loading reports...
              </span>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="divide-y divide-border lg:hidden">
                {reports.length === 0 && <NoReports />}
                {reports.map((report) => (
                  <div key={report.id} className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <EntityTypeBadge entityType={report.entity_type} />
                          <ReportStatusBadge status={report.status} />
                          {report.entity_type === "event" && (
                            <EventStatusBadge status={report.entity_status} />
                          )}
                        </div>
                        <p className="text-sm font-medium">
                          {REASON_LABELS[report.reason] || report.reason}
                        </p>
                        {report.description && (
                          <p className="line-clamp-2 text-sm text-muted-foreground">
                            {report.description}
                          </p>
                        )}
                        <EntityLink
                          type={report.entity_type}
                          id={report.entity_id}
                          newTab
                          className="block break-all text-xs text-primary"
                        >
                          {report.entity_id}
                        </EntityLink>
                        <p className="break-all text-xs text-muted-foreground">
                          {report.reporter_email || report.user_id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(report.created_at)}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDetail(report)}
                          title="View and manage report"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDeleteModal(report)}
                          title="Delete report"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableColumnHeader
                        label="Created"
                        sortDir={sortOrder}
                        onSort={toggleSortOrder}
                      />
                      <TableColumnHeader label="Entity" filter={entityTypeColumnFilter} />
                      <TableColumnHeader label="Reason" filter={reasonColumnFilter} />
                      <TableHead>Description</TableHead>
                      <TableColumnHeader label="Reporter" filter={reporterColumnFilter} />
                      <TableColumnHeader label="Status" filter={statusColumnFilter} />
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <NoReports />
                        </TableCell>
                      </TableRow>
                    )}
                    {reports.map((report) => (
                      <TableRow
                        key={report.id}
                        className="cursor-pointer"
                        onClick={() => handleOpenDetail(report)}
                      >
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {formatDateTime(report.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-1">
                              <EntityTypeBadge entityType={report.entity_type} />
                              {report.entity_type === "event" && (
                                <EventStatusBadge status={report.entity_status} />
                              )}
                            </div>
                            <EntityLink
                              type={report.entity_type}
                              id={report.entity_id}
                              newTab
                              onClick={(e) => e.stopPropagation()}
                              title={`Open ${report.entity_type} ${report.entity_id}`}
                              className="block max-w-[10rem] truncate text-xs text-primary"
                            >
                              {report.entity_id}
                            </EntityLink>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {REASON_LABELS[report.reason] || report.reason}
                        </TableCell>
                        <TableCell>
                          <p className="max-w-[16rem] truncate text-sm text-muted-foreground">
                            {report.description || "-"}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm">
                          {report.reporter_email || report.user_id}
                        </TableCell>
                        <TableCell>
                          <ReportStatusBadge status={report.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div
                            className="flex items-center justify-end gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDetail(report)}
                              title="View and manage report"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDeleteModal(report)}
                              title="Delete report"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={pagination.total_pages || 0}
        onPageChange={setPage}
      />

      {/* Detail / manage modal */}
      <Modal open={isDetailOpen} onOpenChange={(open) => !open && closeDetail()}>
        <ModalContent title="Report Details">
          {selectedReport && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <EntityTypeBadge entityType={selectedReport.entity_type} />
                <ReportStatusBadge status={selectedReport.status} />
                {selectedReport.entity_type === "event" && (
                  <EventStatusBadge status={selectedReport.entity_status} />
                )}
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(selectedReport.created_at)}
                </span>
              </div>

              {/* Reported entity */}
              <div className="rounded-md border border-border p-3">
                <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                  Reported {selectedReport.entity_type}
                </p>
                {entityLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading entity...
                  </div>
                ) : entityError ? (
                  <p className="text-sm text-destructive">{entityError}</p>
                ) : (
                  <p className="font-medium">{entityName}</p>
                )}
                <p className="mt-1 break-all text-xs text-muted-foreground">
                  ID: {selectedReport.entity_id}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  <Link
                    to={getAdminEntityUrl(
                      selectedReport.entity_type,
                      selectedReport.entity_id
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    Open {ENTITY_TYPE_META[selectedReport.entity_type]?.label?.toLowerCase() || "item"}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  {eventWebUrl && (
                    <a
                      href={eventWebUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      View on Applauss
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    Reason
                  </p>
                  <p className="text-sm">
                    {REASON_LABELS[selectedReport.reason] || selectedReport.reason}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    Reporter
                  </p>
                  <p className="break-all text-sm">
                    {selectedReport.reporter_email || selectedReport.user_id}
                  </p>
                </div>
              </div>

              {selectedReport.description && (
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    Description
                  </p>
                  <p className="whitespace-pre-wrap text-sm">
                    {selectedReport.description}
                  </p>
                </div>
              )}

              {proposal && (
                <div className="space-y-2 rounded-md border border-input bg-muted/40 p-3">
                  <p className="text-xs font-medium uppercase text-muted-foreground">
                    AI proposal
                  </p>
                  {(proposal.source || proposal.research_stage || typeof proposal.confidence === "number") && (
                    <p className="text-xs text-muted-foreground">
                      {proposal.source
                        ? `${proposal.source} · `
                        : proposal.research_stage
                          ? `${proposal.research_stage.replaceAll("_", " ")} · `
                          : ""}
                      {typeof proposal.confidence === "number"
                        ? `${Math.round(proposal.confidence * 100)}% confidence`
                        : ""}
                    </p>
                  )}
                  {proposedUrls.length > 0 ? (
                    <div className="space-y-2">
                      {proposedUrls.map((url) => (
                        <div key={url} className="flex items-start gap-2">
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 flex-1 break-all text-sm text-primary underline"
                          >
                            {url}
                          </a>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={Boolean(acceptingUrl)}
                            onClick={() => handleAcceptUrl(url)}
                          >
                            {acceptingUrl === url ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Accept"
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    proposal.diagnosis && (
                      <p className="text-sm">
                        No ticket found — {proposal.diagnosis.category}
                        {proposal.diagnosis.detail ? `: ${proposal.diagnosis.detail}` : ""}
                      </p>
                    )
                  )}
                  {proposal.notes && (
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {proposal.notes}
                    </p>
                  )}
                  {proposal.generated_at && (
                    <p className="text-xs text-muted-foreground">
                      Generated {formatDateTime(proposal.generated_at)}
                    </p>
                  )}
                </div>
              )}

              {selectedReport.resolved_at && (
                <p className="text-xs text-muted-foreground">
                  Resolved {formatDateTime(selectedReport.resolved_at)}
                  {selectedReport.resolved_by && ` by ${selectedReport.resolved_by}`}
                </p>
              )}

              <Select
                label="Status"
                name="status"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                options={statusSelectOptions}
              />

              <div>
                <label
                  htmlFor="admin_notes"
                  className="mb-1 block text-sm font-medium"
                >
                  Admin notes
                </label>
                <textarea
                  id="admin_notes"
                  rows={3}
                  maxLength={2000}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notes about the resolution..."
                  className="w-full rounded-md border border-input bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <ModalFooter>
                <Button variant="outline" onClick={closeDetail} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <ModalContent title="Delete Report">
          <p className="text-muted-foreground">
            Are you sure you want to delete this report? This action cannot be
            undone.
          </p>
          <ModalFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedReport(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create report modal */}
      <CreateReportModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={handleReportCreated}
      />
    </div>
  );
}
